/**
 * Spese › cosa c'è da pagare adesso (26/10/2026, os-planning/ridisegno/CAPITOLATO.md).
 *
 * La domanda: cosa dobbiamo pagare noi, e cosa invece parte da solo?
 * La forma: due liste diverse, perché sono due lavori diversi.
 *   1. "Da pagare": fatture dei fornitori, contanti e spese fisse che non partono da sole
 *      (affitto con bonifico, pagoPA). Qui c'è un bonifico da fare: logo, a chi, da quando,
 *      quanto, "Segna pagata".
 *   2. "Addebiti automatici da spuntare": le spese fisse con addebito o carta la cui data è
 *      passata. Non sono debiti: la banca li ha già presi (o doveva), va solo segnato. Chiusi
 *      in una riga che si apre, con il movimento trovato in banca e "Conferma".
 * Prima (SpeseDaPagare.tsx, che resta nel codice) le due cose stavano mescolate fra le
 * "26 spese scadute" con fascia, ordina, filtra e "per fornitore": sembrava che non pagassimo.
 * Gli addebiti automatici in arrivo non si ripetono qui: stanno nel calendario e, col loro
 * giorno, nelle spese fisse qui sotto.
 */
import { useMemo, useState } from 'react';
import { Icon } from '../../../components/ui/Icon';
import { BrandLogo, brandFor } from '../../../components/ui/BrandLogo';
import { Chip } from '../../../components/ui/Chip';
import { leggibile } from '../../../components/mobile/MobileKit';
import { fmtEur, fmtDate } from '../../../lib/utils';
import { durata, giorniDaOggi, giornoInParole, ilGiorno, alGiorno, dalGiorno, delGiorno, quando, type DaPagare, type SpesaRow } from '../fatture/fattureApi';
import { AzioniSpesa, Suggerimento, descrizione, movimentoDi } from './SpeseDaPagare';

/** Parte da solo: spesa fissa con addebito diretto o carta. */
export const automatico = (r: SpesaRow) => r.source === 'RECURRING' && (r.method === 'ADDEBITO' || r.method === 'CARTA');

/** Le due liste: quello che paghiamo noi e gli addebiti automatici già passati. */
export function divideSpese(d: DaPagare) {
  const daPagare = d.items.filter((r) => !automatico(r));
  const daSpuntare = d.items.filter((r) => automatico(r) && r.gruppo === 'SCADUTE');
  return { daPagare, daSpuntare };
}

export function SpeseDaPagareOra({ data, canWrite }: { data: DaPagare; canWrite: boolean }) {
  const { daPagare, daSpuntare } = useMemo(() => divideSpese(data), [data]);
  const [aperti, setAperti] = useState(false);
  const trovati = daSpuntare.filter((r) => movimentoDi(r)).length;

  return (
    <>
      <section className="ft-blocco" aria-label="Da pagare">
        <h2 className="ft-h2"><Icon name="receipt" size={15} /> Da pagare <span className="ft-h2-sub">fatture dei fornitori e spese che non partono da sole</span></h2>
        {daPagare.length === 0
          ? <p className="ft-tuttook"><Icon name="check" size={14} /> Niente da pagare a mano nei prossimi 30 giorni.</p>
          : (
            <div className="ft-lista">
              {daPagare.map((r) => <RigaDaPagare key={r.key} r={r} canWrite={canWrite} />)}
            </div>
          )}
      </section>

      {daSpuntare.length > 0 && (
        <section className="ft-blocco sp-spunta" aria-label="Addebiti automatici da spuntare">
          <button type="button" className="sp-spunta-hd" aria-expanded={aperti} onClick={() => setAperti((v) => !v)}>
            <span className="sp-spunta-n">{daSpuntare.length}</span>
            <span className="min-w-0">
              <span className="t">Addebiti automatici passati, da spuntare</span>
              <span className="s">
                Sono spese fisse che la banca prende da sola: non sono debiti, va solo segnato che sono state pagate.
                {trovati > 0 && <> Per {trovati === 1 ? 'una' : trovati} c'è già il movimento in banca.</>}
              </span>
            </span>
            <Icon name={aperti ? 'chevronU' : 'chevronD'} size={13} className="mute" />
          </button>
          {aperti && (
            <div className="ft-lista sp-spunta-lista">
              {daSpuntare.map((r) => <RigaDaSpuntare key={r.key} r={r} canWrite={canWrite} />)}
            </div>
          )}
        </section>
      )}
    </>
  );
}

/** Il marchio dal fornitore prima che dalla descrizione ("Google Workspace Masamune" è di Google). */
const marchio = (r: SpesaRow) => brandFor(r.counterparty) ?? brandFor(r.description);

function RigaDaPagare({ r, canWrite }: { r: SpesaRow; canWrite: boolean }) {
  const g = giorniDaOggi(r.dueDate);
  const scaduta = g != null && g < 0;
  const cosa = descrizione(r);
  return (
    <div className="ft-riga sp-riga">
      <BrandLogo brand={marchio(r)} name={leggibile(r.counterparty)} size={36} />
      <span className="ft-chi">
        <span className="t">
          {leggibile(r.counterparty)}
          {r.entity === 'PERSONALE_ANDREA' && <Chip tone="ghost">Di Andrea</Chip>}
        </span>
        <span className="s" title={cosa ?? undefined}>{r.tipo === 'SPESA_FISSA' ? `spesa fissa${cosa ? `, ${cosa}` : ''}` : r.tipo === 'CONTANTI' ? `in contanti${cosa ? `, ${cosa}` : ''}` : cosa ?? 'fattura'}</span>
      </span>
      <span className={`ft-quando${scaduta ? ' is-alert' : ''}`} title={r.dueDate ? `Scadenza: ${fmtDate(r.dueDate)}` : undefined}>
        <span className="t">{scaduta ? `scaduta da ${durata(g!)}` : r.dueDate ? `da pagare ${quando(r.dueDate)}` : 'senza scadenza'}</span>
        {r.dueDate && <span className="s">{scaduta ? `era ${ilGiorno(r.dueDate)}` : `${ilGiorno(r.dueDate)}`}</span>}
      </span>
      <span className="ft-cifra">€ {fmtEur(r.amount, true)}</span>
      <span className="ft-azioni"><AzioniSpesa r={r} canWrite={canWrite} /></span>
      {r.suggerimento && <div className="sp-sugg"><Suggerimento r={r} canWrite={canWrite} /></div>}
    </div>
  );
}

function RigaDaSpuntare({ r, canWrite }: { r: SpesaRow; canWrite: boolean }) {
  const mov = movimentoDi(r);
  return (
    <div className="ft-riga sp-riga">
      <BrandLogo brand={marchio(r)} name={r.counterparty} size={32} />
      <span className="ft-chi">
        <span className="t">{r.description && r.description !== r.counterparty ? r.description : r.counterparty}</span>
        {/* 27/10/2026: era "in banca non risulta", ma per Amazon Prime, Apple, WaAutoReply e Google
            gli addebiti in banca ci sono e non tornano (tre addebiti per una scheda sola, o l'addebito
            già collegato alla fattura): la frase dice solo quello che è vero in tutti i casi. */}
        <span className="s">{mov ? 'in banca c\'è un movimento: controlla e conferma' : 'in banca nessun addebito che torni: controlla, poi segna pagata'}</span>
      </span>
      <span className="ft-quando"><span className="t">{giornoInParole(r.dueDate)}</span></span>
      <span className="ft-cifra">€ {fmtEur(r.amount, true)}</span>
      <span className="ft-azioni">{!mov && <AzioniSpesa r={r} canWrite={canWrite} />}</span>
      {mov && <div className="sp-sugg"><Suggerimento r={r} canWrite={canWrite} /></div>}
    </div>
  );
}
